import type { ZplCommand } from './zpl-command'

/**
 * Serializes a `ZplCommand[]` to a single ZPL II string.
 *
 * Wraps the command stream in `^XA` … `^XZ` framing, with one ZPL block per
 * command separated by `\n` for readability. The serializer is pure: it
 * preserves the input order and does NOT reorder setup commands
 * (`^LH`/`^LL`/`^PW`). That ordering is `generateZpl`'s responsibility
 * (sub-task 2a.10.13).
 *
 * Phase-1 simplifications worth knowing:
 *   - **Text**: uses Zebra's scalable font 0 (`^A0N`). Dot dimensions are
 *     computed upstream in `build-commands.ts` from the printer profile's
 *     `dpi`; the serializer no longer carries a dpi assumption. Custom
 *     `fontId`s are accepted but currently rendered the same way; lint
 *     (sub-task 2a.10.15) surfaces a warning if `fontId !== '0'`.
 *   - **Barcode**: `code-128`, `ean-13`, `qr`, `gs1-128`, `itf` are routed to
 *     their respective `^BC` / `^BE` / `^BQ` / `^B2` forms. Any other
 *     symbology emits a `^FX … not yet implemented …^FS` comment fallback
 *     (no-op at print time) instead of throwing — Phase 1 doesn't ship more.
 *   - **Image**: emitted via `^GFA` (Graphic Field, ASCII-hex). The
 *     `bytesPerRow` / `totalBytes` arithmetic assumes the caller has already
 *     packed the bitmap into row-major 1-bpp bytes (1 dot = 1 bit,
 *     most-significant-bit first within each byte). That packing is the
 *     image-decoder's job upstream of `buildCommands`.
 *   - **Raw**: passed through verbatim; the caller owns the validity.
 *
 * Pure: no I/O, no globals, no mutation of inputs.
 */
export function serializeZpl(commands: ZplCommand[]): string {
  const lines: string[] = ['^XA']
  for (const cmd of commands) {
    lines.push(renderCommand(cmd))
  }
  lines.push('^XZ')
  return lines.join('\n')
}

function renderCommand(cmd: ZplCommand): string {
  switch (cmd.kind) {
    case 'label-home':
      return `^LH${cmd.x},${cmd.y}`
    case 'label-length':
      return `^LL${cmd.dots}`
    case 'print-width':
      return `^PW${cmd.dots}`
    case 'field-block':
      return `^FB${cmd.widthDots},${cmd.maxLines},${cmd.lineSpacingDots},${cmd.alignment},${cmd.hangingIndentDots}`
    case 'field':
      return `^FO${cmd.x},${cmd.y}^FD${cmd.content}^FS`
    case 'text': {
      const orientation = cmd.rotation ?? 'N'
      const fr = cmd.reverseField === true ? '^FR' : ''
      return `^FO${cmd.x},${cmd.y}^A0${orientation},${cmd.fontHeightDots},${cmd.fontWidthDots}${fr}^FD${cmd.text}^FS`
    }
    case 'barcode':
      return renderBarcode(cmd)
    case 'image':
      return renderImage(cmd)
    case 'graphic-box':
      return `^FO${cmd.x},${cmd.y}^GB${cmd.widthDots},${cmd.heightDots},${cmd.thicknessDots}^FS`
    case 'raw':
      return cmd.value
    case 'print-quantity':
      // Abbreviated ^PQq form: pause-count/replicates/override params are
      // intentionally omitted and fall back to printer defaults (0,0,N).
      return `^PQ${cmd.quantity}`
    default: {
      // Exhaustiveness guard over `ZplCommand['kind']`. If a new variant
      // lands in the union and isn't wired up here, TypeScript fails at
      // compile time.
      const _exhaustive: never = cmd
      throw new Error(`serializeZpl: unknown command ${JSON.stringify(_exhaustive)}`)
    }
  }
}

/**
 * Clamp a requested module width (or QR magnification) into ZPL's legal
 * 1–10 range, falling back to `fallback` when the field is undefined. Shared
 * by the `^BY` module-width and the QR `^BQ` magnification arms.
 */
function clampModuleWidthDots(dots: number | undefined, fallback: number): number {
  return Math.max(1, Math.min(10, dots ?? fallback))
}

function renderBarcode(cmd: Extract<ZplCommand, { kind: 'barcode' }>): string {
  const heightDots = cmd.heightDots ?? 96
  const o = cmd.rotation ?? 'N'
  const hri = cmd.humanReadable === true ? 'Y' : 'N'
  // `^FR` placed AFTER the symbology selector and BEFORE `^FD` reverses the
  // entire field's print colour. Barcodes-on-black are rarely scannable, so
  // we expose this for parity with the text path but expect callers to leave
  // it off — see `commands-from-plans.ts` for the gate that controls it.
  const fr = cmd.reverseField === true ? '^FR' : ''
  const by = `^BY${clampModuleWidthDots(cmd.moduleWidthDots, 2)}`
  switch (cmd.symbology) {
    case 'code-128':
      // ^BC<orientation>,<height>,<HRI>,<HRI above>,<check digit>
      // Orientation: N (normal). check digit: N (Code-128 has its own).
      return `^FO${cmd.x},${cmd.y}${by}^BC${o},${heightDots},${hri},N,N${fr}^FD${cmd.data}^FS`
    case 'ean-13': {
      // ^BE<orientation>,<height>,<HRI>,<HRI above>
      // EAN-13 shows HRI by default; honour explicit `false`.
      const eanHri = cmd.humanReadable === false ? 'N' : 'Y'
      return `^FO${cmd.x},${cmd.y}${by}^BE${o},${heightDots},${eanHri},N${fr}^FD${cmd.data}^FS`
    }
    case 'qr': {
      // ^BQ<orientation>,<model>,<magnification>
      // Model 2 is the standard; magnification 1-10 (default 4).
      // ^FDLA, — error-correction L, input mode A (alphanumeric/auto).
      // QR uses magnification (not ^BY); clamp to valid 1-10 range.
      const magnification = clampModuleWidthDots(cmd.moduleWidthDots, 4)
      return `^FO${cmd.x},${cmd.y}^BQ${o},2,${magnification}${fr}^FDLA,${cmd.data}^FS`
    }
    case 'gs1-128':
      // Mode A (auto). Data carries a leading `>8` FNC1 (and `>8` separators
      // after variable-length AIs) so the printer parses GS1 AIs and formats
      // the HRI itself. No `^FH` — the payload uses `>8`, not hex escapes.
      return `^FO${cmd.x},${cmd.y}${by}^BC${o},${heightDots},${hri},N,N,A${fr}^FD${cmd.data}^FS`
    case 'itf':
      // ^B2<orientation>,<height>,<HRI>,<HRI above>,<check digit>
      // check digit: N — cmd.data already carries the full GTIN-14 including
      // the GS1 mod-10 check digit (see barcodes/itf-14.ts); we validate it
      // ourselves at lint/derive time and must NOT let the printer recompute
      // or append its own.
      // NOTE: GS1's ITF-14 profile conventionally wants bearer bars framing
      // the symbol (protects against ink-spread on corrugated cartons);
      // ^B2 has no bearer-bar flag, and this Phase-1 slice does not emit the
      // extra ^GB framing that would be needed for full GS1 conformance —
      // deferred (see issue #129 discussion). Plain Interleaved-2-of-5 today.
      return `^FO${cmd.x},${cmd.y}${by}^B2${o},${heightDots},${hri},N,N${fr}^FD${cmd.data}^FS`
    default:
      // Phase 1 doesn't ship other symbologies. Emit a ZPL comment so the
      // surrounding format stays valid but the printer renders nothing.
      return `^FX symbology ${cmd.symbology} not yet implemented in Phase 1 serializer^FS`
  }
}

function renderImage(cmd: Extract<ZplCommand, { kind: 'image' }>): string {
  const bytesPerRow = Math.ceil(cmd.widthDots / 8)
  const totalBytes = bytesPerRow * cmd.heightDots
  const hex = bufferToHex(cmd.data)
  return `^FO${cmd.x},${cmd.y}^GFA,${totalBytes},${totalBytes},${bytesPerRow},${hex}^FS`
}

function bufferToHex(buf: Uint8Array): string {
  let out = ''
  for (let i = 0; i < buf.length; i++) {
    const byte = buf[i]!
    out += byte.toString(16).padStart(2, '0').toUpperCase()
  }
  return out
}
